import React, { useEffect, useState }  from 'react'
import { Grid } from '@material-ui/core'
import { FILTER_TYPE, Title, DataTable, DataRow, Search, NewButton, newTheme, Loading } from 'prace-common-components'
import { $Centers, $Paper, $ChipContainer, $Chip, $CenterName } from './styles'
import { CenterDetails } from './CenterDetails'
import { useSearchCentersMutation } from 'store/api/partitions'

const limit = 999

export const PartitionManager = () => {
  const [page, setPage] = useState(1)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedCenter, setSelectedCenter] = useState<number | undefined>(-1)
  const [searchCenters, { data: centers = { centers: [], count: 0 }, isUninitialized}] = useSearchCentersMutation()

  const pagination = { count: centers?.count, page }
  const stateCenters = centers?.centers

  useEffect(() => { setPage(1) }, [debouncedSearch])
  useEffect(() => {
    const getCenters = async () => {
      try {
        await searchCenters({ search: debouncedSearch || '', page: page - 1, pageSize: limit }).unwrap()
      } catch (err) {
        console.log(err)
      }
    }
    getCenters().catch((err) => console.log(err))
  }, [searchCenters, debouncedSearch, page])

  /** change current table page */
  const handleChangePage = (page: number) => setPage(page)
  const handleSearch = (value: string) => setDebouncedSearch(value)
  const handleRowClick = (idx: number) => () => setSelectedCenter(idx)
  const handleCenterClose = async (fetchAgain?: boolean) => {
    setSelectedCenter(-1)
    if(fetchAgain) {
      try {
        await searchCenters({ search: debouncedSearch || '', page: page - 1, pageSize: limit })
      } catch (err) {
        console.log(err)
      }
    }
  }

  const columns = React.useMemo(() => [
    {
      key: 'name',
      type: FILTER_TYPE.TEXT,
      name: 'Center Name',
      formatter(props: { row: DataRow }) {
        const { name, idx } = props.row
        return <$CenterName onClick={handleRowClick(idx)}>{name}</$CenterName>
      },
    },
    { key: 'ccr', type: FILTER_TYPE.NONE, name: 'CCR', noSort: true,
      formatter(props: { row: DataRow }) {
        const { user, idx } = props.row
        return <$CenterName onClick={handleRowClick(idx)}>{user?.firstName || '-'} {user?.lastName || '-'}</$CenterName>
      },
    },
    { key: 'email', type: FILTER_TYPE.NONE, name: 'CCR email', noSort: true,
      formatter(props: { row: DataRow }) {
        const { user, idx } = props.row
        return <$CenterName onClick={handleRowClick(idx)}>{user?.email || '-'}</$CenterName>
      },
    },
    { key: 'partitionNames', type: FILTER_TYPE.TEXT, name: 'Partitions', width: 220, noSort: true,
      formatter(props: { row: DataRow }) {
        const { partitionNames, idx } = props.row
        return <$CenterName onClick={handleRowClick(idx)}>{partitionNames}</$CenterName>
      },
    },
    { key: 'partitionTypes', type: FILTER_TYPE.TEXT, name: 'Types', width: 160, noSort: true,
      formatter(props: { row: DataRow }) {
        const { partitionTypes, idx } = props.row
        return <$CenterName onClick={handleRowClick(idx)}>{partitionTypes}</$CenterName>
      },
    },
    { key: 'reviewers', type: FILTER_TYPE.NONE, name: 'Reviewers', width: 120, noSort: true,
      formatter(props: { row: DataRow }) {
        const { reviewers, idx } = props.row
        return <$CenterName onClick={handleRowClick(idx)}>{reviewers?.length ?? 0}</$CenterName>
      },
    },
    { key: 'disabled', type: FILTER_TYPE.NONE, name: 'Status', width: 100, noSort: true,
      formatter(props: { row: DataRow }) {
        const { disabled, idx } = props.row
        return <$ChipContainer onClick={handleRowClick(idx)}>
          <$Chip color={disabled ? newTheme.colors.secondaries.light : newTheme.colors.neutral.light}>{disabled ? 'Disabled' : 'Active'}</$Chip>
        </$ChipContainer>
      },
    },
  ], [])

  const dataRows = React.useMemo(() => (stateCenters?.map((center, idx: number) => ({
    ...center,
    /* Placeholders, not empty strings: DataTable treats a falsy cell as "no value" and passes the
     * row through every filter, so centers with no partitions would survive a type filter. */
    partitionNames: center.partitionNames || '-',
    partitionTypes: center.partitionTypes || '-',
    idx,
  }))), [stateCenters])

  if(isUninitialized) return <Loading />

  const center = (selectedCenter !== undefined && selectedCenter > -1) ? stateCenters[selectedCenter] : undefined

  return (
    <>
      <$Centers>
        <$Paper>
          <Grid container alignItems='center' justifyContent='space-between'>
            <Grid item>
              <Title alternate fontSize={22}>Partition Manager</Title>
            </Grid>
            <NewButton creator onClick={() => setSelectedCenter(undefined)}>Add Center</NewButton>
          </Grid>
          <Search open onChange={handleSearch} placeholder='Search centers'/>
        </$Paper>
        <DataTable
          rows={dataRows}
          columns={columns}
          initialSort={[{
            columnKey: 'name',
            direction: 'ASC',
          }]}
          total={pagination.count}
          rowsPerPage={limit}
          page={page}
          noFilterType
          resizable
          columnWidthsStorageKey='partitionManagerTableColumnWidths'
          onChangePage={handleChangePage}
          showEmptyImg={false}
        />
      </$Centers>
      <CenterDetails
        open={selectedCenter !== -1}
        center={center}
        handleClose={handleCenterClose}
      />
    </>
  )
}
